'use client';

import { useActionState, useEffect } from 'react';
import Form from 'next/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { setForgetPasswordSession } from '@/app/actions/auth/helper';
import { sendSms } from '@/app/actions/sms';
import { ActivityIndicator, TextField } from '@/app/components/shared';
import { getErrorMessage } from '@/app/lib/utils';
import { ForgetPasswordSchema } from './formSchema';

export default function ForgotPasswordForm({ email }: { email?: string }) {
  const t = useTranslations('ForgotPassword');
  const e = useTranslations('Errors');

  const processForm = async (_: any, formData: FormData) => {
    const email = formData.get('email') as string;

    const data = await sendSms(email, 'forgot_password', {
      bypassErrors: true,
    });

    return data;
  };

  const [state, formAction, isPending] = useActionState(processForm, null);

  const {
    register,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      email: email ?? '',
    },
    resolver: zodResolver(ForgetPasswordSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (state?.confirmationMessage && state?.mobileNumber) {
      setForgetPasswordSession(
        state?.verificationCode,
        getValues('email'),
        state?.mobileNumber,
      );
    }
  }, [state]);

  return (
    <Form action={formAction} className="group flex w-full flex-col gap-6">
      <p className="text-center text-lg font-medium leading-8">{t('title')}</p>
      {isPending || (state?.confirmationMessage && state?.mobileNumber) ? (
        <div className="bg-surface/50 absolute inset-0 z-50 grid size-full place-items-center backdrop-blur-lg">
          <ActivityIndicator />
        </div>
      ) : (
        state?.error && (
          <p className="text-center font-medium text-danger">{state?.error}</p>
        )
      )}

      <TextField
        type="email"
        label={t('email')}
        required
        messages={getErrorMessage(errors, 'email', t, e)}
        {...register('email')}
      />
      <button
        disabled={!isValid}
        className={`h-12 w-full rounded-xl font-medium ${isValid ? 'bg-blue-600 text-on-primary' : 'bg-gray-200 text-gray-400'}`}
      >
        {t('send')}
      </button>
    </Form>
  );
}
